import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLazyQuery } from '@apollo/client';
import { Container, List } from '@pella/sash-framework';
import { Image } from '../../';
import { contentstackClient } from '../../../utils/contentstack-client';
import { GET_INSPIRATION_BY_TAXONOMY } from '../../../enums/contentstack-graphql';
import { isValidArray } from '../../../components/utilities/validations';
import { getLocalStorage, removeLocalStorage } from '../../utilities/functions';

import ProjectFilters from '../../template/category-inspiration/filters';
import InspireGrid from '../../template/category-inspiration/inspire-grid';

const InspirationGrid = ({ data, popularPicks, productType, filterCategory, staticTiles, baseFilters }) => {
	const categoryName = filterCategory ? filterCategory[0].toUpperCase() + filterCategory.slice(1) : '';
	const productTypeTag = productType === 'entry-doors' ? 'entry_door' : productType === 'patio-doors' ? 'patio_door' : productType === 'windows' ? 'window' : null;
	const [activeFilters, setActiveFilters] = useState({ base: productTypeTag, ...(categoryName && { [categoryName]: baseFilters?.[0] }) });
	const [inspirationData, setInspirationData] = useState({ entries: staticTiles.nodes || [], totalCount: staticTiles?.totalCount });
	const [skip, setSkip] = useState(0);
	const [tilesToShow, setTilesToShow] = useState(14);
	const [sortBy, setSortBy] = useState('Most Recent');
	const [getInspiration] = useLazyQuery(GET_INSPIRATION_BY_TAXONOMY, { client: contentstackClient, fetchPolicy: 'network-only', onCompleted: (entries) => onLoadComplete(entries?.all_image_detail) });

	useEffect(() => {
		let params = new URLSearchParams(window.location.search);
		let sort = params.get('sort');

		if (sort === 'stl') setSortBy('Shop The Look');

		getInspiration({ variables: { limit: 14, skip, filters: restructureFilters(activeFilters) } });

		let scrollPoint = getLocalStorage('scrollPoint');

		if (scrollPoint && scrollPoint.closeURL && scrollPoint.position) {
			if (typeof window !== 'undefined' && typeof scrollTo !== 'undefined') {
				(window.location.pathname == scrollPoint.closeURL &&
					setTimeout(() => {
						scrollTo(scrollPoint.position.offSetX, scrollPoint.position.offSetY);
					}, 200)) ||
					'';
				removeLocalStorage('scrollPoint');
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps -- preexisting failure, fix when possible
	}, []);

	const onLoadComplete = (data) => {
		if (!isValidArray(data.items)) {
			setInspirationData({ totalCount: 0, entries: [] });
			setSkip(0);
			return;
		}
		let newArr;
		if (skip > 0) {
			newArr = [...inspirationData.entries, ...data.items];
		} else {
			newArr = [...data.items];
		}
		if (sortBy === 'Most Recent') {
			newArr.sort(handleDefaultSort);
		} else {
			newArr.sort(handleShopTheLookSort);
		}
		newArr = insertRtaCard(newArr);
		setInspirationData({ totalCount: data.total, entries: newArr });
		setSkip(skip + data?.items?.length);
	};

	const insertRtaCard = (arr) => {
		if (skip === 0) {
			if (arr?.length < 3) {
				arr.push({ card_type: 'rta' });
			} else {
				arr = [...arr.slice(0, 3), { card_type: 'rta' }, ...arr.slice(3, tilesToShow)];
			}
		} else {
			const card = arr.find((item) => item.card_type === 'rta');
			arr.splice(arr.indexOf(card), 1);
			arr.splice(3, 0, card);
		}

		return arr;
	};

	const handleLoadMoreClick = (event) => {
		event.preventDefault();

		setTilesToShow(tilesToShow + 6);
		getInspiration({ variables: { limit: 6, skip, filters: restructureFilters(activeFilters) } });
	};

	const onSelectChange = (event) => {
		let newFilters = { ...activeFilters };
		newFilters[event.target.name] = event.target.value;

		getInspiration({ variables: { filters: restructureFilters(newFilters), limit: 14, skip: 0 } });

		setActiveFilters(newFilters);
		setTilesToShow(14);
		setSkip(0);
	};

	const onPopularPicksClick = (values) => {
		let newFilters = {};

		values.tags_by_category.forEach((value) => {
			newFilters[value.category] = value.filter_tag;
		});

		getInspiration({ variables: { filters: restructureFilters(newFilters), limit: 14, skip: 0 } });

		setActiveFilters(newFilters);
		setTilesToShow(14);
		setSkip(0);

		const filters = document.getElementById('project-filters');
		filters?.scrollIntoView({ behavior: 'smooth' });
	};

	const onSortChange = (sortType) => {
		let sortedArr = [...inspirationData.entries];
		sortedArr.sort(sortType === 'Most Recent' ? handleDefaultSort : handleShopTheLookSort);

		sortedArr = insertRtaCard(sortedArr);

		setSortBy(sortType);
		setInspirationData({ ...inspirationData, entries: sortedArr });
	};

	const handleDefaultSort = (a, b) => new Date(b.system?.created_at) - new Date(a.system?.created_at);

	const handleShopTheLookSort = (a, b) => {
		if (a.tiles?.tileConnection?.edges?.length > 0 && b.tiles?.tileConnection?.edges?.length > 0) {
			return handleDefaultSort(a, b);
		}

		return a.tiles?.tileConnection?.edges?.length > 0 ? -1 : 1;
	};

	const restructureFilters = (filters) =>
		Object.values(filters)
			.filter((value) => Boolean(value))
			.flat()
			.map((value) => ({
				taxonomies: {
					filter_by: {
						term_in: [...value?.split(', ')],
					},
				},
			}));

	return (
		<div
			className='inspiration-grid py-7 md:py-10 xl:py-[52px]'
			data-testid='inspiration-grid'
		>
			<Container>
				{data?.heading && <h2 className='text-h3 mb-6'>{data.heading}</h2>}

				{isValidArray(popularPicks?.buttons) && (
					<div className='mb-6 md:mb-8'>
						{popularPicks.subheading && <p className='text-overline mb-4 text-neutral-600'>{popularPicks.subheading}</p>}

						<List
							bullets='none'
							className='grid grid-cols-1 gap-4 md:grid-cols-3'
						>
							{popularPicks.buttons.map((filter, index) => (
								<li
									key={index}
									className={index > 2 ? 'hidden md:block' : ''}
								>
									<button
										onClick={() => onPopularPicksClick(filter)}
										className='group relative flex h-[76px] w-full flex-col items-center justify-center overflow-hidden bg-black text-center xl:h-[115px]'
									>
										{filter?.file?.url && (
											<Image
												data={{ image: filter.file }}
												className='!absolute left-0 top-0 !block opacity-[25%] transition-opacity group-hover:opacity-[35%]'
											/>
										)}

										<div className='relative'>
											{filter.subheading && <span className='text-body-2 block text-white'>{filter.subheading}</span>}
											{filter.heading && <span className='text-subtitle block font-bold !text-white xl:text-h3 xl:mb-0'>{filter.heading}</span>}
										</div>
									</button>
								</li>
							))}
						</List>
					</div>
				)}

				<ProjectFilters
					isPro={false}
					onSelectChange={onSelectChange}
					onSortChange={onSortChange}
					activeFilters={activeFilters}
					activeSort={sortBy}
					productType={productType}
				/>

				<InspireGrid
					tiles={inspirationData.entries}
					tilesToShow={tilesToShow}
					totalCount={inspirationData.totalCount}
					onLoadMoreClick={handleLoadMoreClick}
				/>
			</Container>
		</div>
	);
};

InspirationGrid.propTypes = {
	data: PropTypes.object,
	popularPicks: PropTypes.object,
	productType: PropTypes.string,
	filterCategory: PropTypes.string,
	staticTiles: PropTypes.object,
	baseFilters: PropTypes.array,
};

export default InspirationGrid;
